import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { LeaderboardUserRankModel, OrderType } from '@/api/model'

/** Vom Board-Endpunkt akzeptierte Sortierfelder (s. AggregateLeaderboardLogic). */
export const LeaderboardSortFields = {
  Rank: 'Rank',
  FirstName: 'FirstName',
  LastName: 'LastName',
  Position: 'Position',
  EmployeeNumber: 'EmployeeNumber',
} as const

interface Props {
  isLoading: boolean
  isError: boolean
  rows: LeaderboardUserRankModel[] | undefined | null
  currentSort?: string
  currentOrder?: OrderType
  onSort: (sort?: string, order?: OrderType) => void
}

const COL_COUNT = 6

export function LeaderboardBoardTable({
  isLoading,
  isError,
  rows,
  currentSort,
  currentOrder,
  onSort,
}: Props) {
  const { t } = useTranslation(['service-modules-leaderboard', 'common'])
  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <SortableTableHead field={LeaderboardSortFields.Rank} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort} className="w-20">
            {t('leaderboard.table.rank')}
          </SortableTableHead>
          <SortableTableHead field={LeaderboardSortFields.FirstName} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.firstName', { ns: 'common' })}
          </SortableTableHead>
          <SortableTableHead field={LeaderboardSortFields.LastName} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.lastName', { ns: 'common' })}
          </SortableTableHead>
          <SortableTableHead field={LeaderboardSortFields.Position} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.position', { ns: 'common' })}
          </SortableTableHead>
          <SortableTableHead field={LeaderboardSortFields.EmployeeNumber} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.employeeNumber', { ns: 'common' })}
          </SortableTableHead>
          <TableHead className="text-right">{t('leaderboard.table.score')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable
          isLoading={isLoading}
          isError={isError}
          isEmpty={!rows || rows.length === 0}
          colSpan={COL_COUNT}
          loadingMessage={t('leaderboard.table.loading')}
          errorMessage={t('leaderboard.table.loadError')}
          emptyMessage={t('leaderboard.table.empty')}
        >
          {rows?.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-medium tabular-nums">{row.rank}</TableCell>
            <TableCell>{row.firstName || '—'}</TableCell>
            <TableCell>{row.lastName || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{row.position || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{row.employeeNumber || '—'}</TableCell>
            <TableCell className="text-right font-medium tabular-nums">{row.score}</TableCell>
          </TableRow>
          ))}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
